/**
 * Incremental schedule cache with React-compatible subscription API.
 * Orchestrates taskDuration + multitasking + employeeStats + doubleBooking.
 */

import type { Task, Employee } from '../../types';
import type { ScheduledTask, EmployeeStats, DoubleBookingWarning, ScheduleResult } from './types';
import { computeTaskBaseDuration, computeTaskInputHash, type TaskBaseDuration } from './taskDuration';
import { applyMultitaskingPenalties } from './multitasking';
import { calculateEmployeeStats } from './employeeStats';
import { detectDoubleBookings } from './doubleBooking';

// ============================================
// Snapshot type (for useSyncExternalStore)
// ============================================

export interface ScheduleCacheSnapshot {
  scheduledTasks: Map<string, ScheduledTask>;
  totalDays: number;
  employeeStats: Map<string, EmployeeStats>;
  doubleBookingWarnings: DoubleBookingWarning[];
  lastComputedAt: number;
}

// ============================================
// ScheduleCache class
// ============================================

export class ScheduleCache {
  private baseDurations = new Map<string, TaskBaseDuration>();
  private hashCache = new Map<string, string>();
  private scheduledTasks = new Map<string, ScheduledTask>();
  private employeeStatsMap = new Map<string, EmployeeStats>();
  private doubleBookings: DoubleBookingWarning[] = [];
  private totalDays = 0;
  private lastComputedAt = 0;
  private listeners = new Set<() => void>();
  private snapshot: ScheduleCacheSnapshot | null = null;

  compute(tasks: Task[], employees: Employee[], boardStartDate: string): void {
    let changed = false;
    const currentTaskIds = new Set(tasks.map(t => t.id));

    // Remove stale tasks
    for (const taskId of this.hashCache.keys()) {
      if (!currentTaskIds.has(taskId)) {
        this.hashCache.delete(taskId);
        this.baseDurations.delete(taskId);
        this.scheduledTasks.delete(taskId);
        changed = true;
      }
    }

    // Step 1: Incremental base duration computation
    const affectedEmployees = new Set<string>();

    for (const task of tasks) {
      const newHash = computeTaskInputHash(task, boardStartDate);
      const oldHash = this.hashCache.get(task.id);

      if (oldHash === newHash) continue; // Unchanged

      // Recompute this task
      const baseDur = computeTaskBaseDuration(task, boardStartDate);
      this.baseDurations.set(task.id, baseDur);
      this.hashCache.set(task.id, newHash);
      changed = true;

      // Track affected employees for multitasking recalc
      for (const empId of task.assigneeIds) {
        affectedEmployees.add(empId);
      }
    }

    if (!changed) return; // Nothing to do

    // Step 2: Apply multitasking penalties
    const multitaskingResults = applyMultitaskingPenalties(this.baseDurations, tasks);

    // Step 3: Build ScheduledTask objects with reference stability
    let maxEndDay = 0;
    const newScheduledTasks = new Map<string, ScheduledTask>();

    for (const task of tasks) {
      const baseDur = this.baseDurations.get(task.id);
      if (!baseDur) continue;

      const mtResult = multitaskingResults.get(task.id);
      const adjustedDays = mtResult?.adjustedEffectiveDays ?? baseDur.effectiveDays;
      const multitaskingDayCount = mtResult?.multitaskingDayCount ?? 0;
      const endDay = baseDur.startDay + adjustedDays - 1;
      maxEndDay = Math.max(maxEndDay, endDay);

      const existing = this.scheduledTasks.get(task.id);

      // Check if we can reuse the existing reference
      if (
        existing &&
        existing.startDay === baseDur.startDay &&
        existing.baseDays === baseDur.baseDays &&
        existing.effectiveDays === adjustedDays &&
        existing.endDay === endDay &&
        existing.totalSpeed === baseDur.totalSpeed &&
        existing.totalRisk === baseDur.totalRisk &&
        existing.teamWeeks === baseDur.teamWeeks &&
        existing.bestCaseWeeks === baseDur.bestCaseWeeks &&
        existing.multitaskingDayCount === multitaskingDayCount &&
        existing.column === task.column &&
        existing.assigneeIds.join(',') === task.assigneeIds.join(',')
      ) {
        newScheduledTasks.set(task.id, existing);
        continue;
      }

      newScheduledTasks.set(task.id, {
        taskId: task.id,
        assigneeIds: task.assigneeIds,
        primaryAssigneeId: task.assigneeIds[0] ?? '',
        column: task.column,
        startDay: baseDur.startDay,
        baseDays: baseDur.baseDays,
        effectiveDays: adjustedDays,
        teamWeeks: baseDur.teamWeeks,
        endDay,
        totalSpeed: baseDur.totalSpeed,
        totalRisk: baseDur.totalRisk,
        contributionDetails: baseDur.contributionDetails,
        multitaskingDayCount,
        bestCaseWeeks: baseDur.bestCaseWeeks,
      });
    }

    this.scheduledTasks = newScheduledTasks;
    this.totalDays = maxEndDay > 0 ? maxEndDay + 1 : 0;

    // Step 4: Employee stats + double bookings (cheap, O(n))
    const scheduleResult: ScheduleResult = {
      scheduledTasks: this.scheduledTasks,
      totalDays: this.totalDays,
    };
    this.employeeStatsMap = calculateEmployeeStats(scheduleResult, tasks);
    this.doubleBookings = detectDoubleBookings(tasks, scheduleResult, employees);

    this.lastComputedAt = Date.now();
    this.snapshot = null; // Invalidate snapshot
    this.notifyListeners();
  }

  // ============================================
  // Accessors
  // ============================================

  getScheduledTask(taskId: string): ScheduledTask | undefined {
    return this.scheduledTasks.get(taskId);
  }

  getScheduledTasks(): Map<string, ScheduledTask> {
    return this.scheduledTasks;
  }

  getEmployeeStats(): Map<string, EmployeeStats> {
    return this.employeeStatsMap;
  }

  getDoubleBookingWarnings(): DoubleBookingWarning[] {
    return this.doubleBookings;
  }

  getTotalDays(): number {
    return this.totalDays;
  }

  // ============================================
  // React: useSyncExternalStore compatible
  // ============================================

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = (): ScheduleCacheSnapshot => {
    if (!this.snapshot) {
      this.snapshot = {
        scheduledTasks: this.scheduledTasks,
        totalDays: this.totalDays,
        employeeStats: this.employeeStatsMap,
        doubleBookingWarnings: this.doubleBookings,
        lastComputedAt: this.lastComputedAt,
      };
    }
    return this.snapshot;
  };

  dispose(): void {
    this.listeners.clear();
    this.baseDurations.clear();
    this.hashCache.clear();
    this.scheduledTasks.clear();
    this.employeeStatsMap.clear();
    this.doubleBookings = [];
    this.snapshot = null;
  }

  // ============================================
  // Internal
  // ============================================

  private notifyListeners(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
